import { useState, useRef } from "react";
import isEqual from "lodash-es/isEqual";
import { getCheckout_checkout } from "../web-nextjs/__generated__/getCheckout";

export function useCheckout() {
  const [checkout, setCheckout] = useState<getCheckout_checkout>();
  const checkoutRef = useRef<getCheckout_checkout>();

  return {
    checkout,
    checkoutRef,
    setCheckout(checkout) {
      checkoutRef.current = checkout;
      setCheckout(checkout);
    },
  };
}

interface ActionProps {
  id: string;
  quantity?: number;
  ingredients?: {
    id: string;
    name: string;
    amount: number;
  }[];
}

export function useItemsToChange() {
  const initialItemsToChange = [];
  const items = useRef(initialItemsToChange);
  const [itemsState, setItemsState] = useState(initialItemsToChange);

  function updateItemsToChange({ id, quantity, ingredients }: ActionProps) {
    const newItems = [...items.current];

    function mutateNewItems({ id, quantity, ingredients }: ActionProps) {
      const prevItemIndex = newItems.findIndex((item) => {
        const ingredientsEqual = isEqual(ingredients, item.ingredients);
        return item.id === id && ingredientsEqual;
      });

      if (prevItemIndex >= 0) {
        newItems[prevItemIndex] = {
          ...newItems[prevItemIndex],
          quantity: newItems[prevItemIndex].quantity + quantity,
        };
      } else {
        newItems.push({
          id,
          ingredients,
          quantity,
        });
      }

      if (ingredients) {
        ingredients.forEach(({ id, amount }) => {
          mutateNewItems({
            id,
            quantity: quantity > 0 ? amount : -amount,
            ingredients: [],
          });
        });
      }
    }
    mutateNewItems({ id, quantity, ingredients });
    items.current = newItems;

    setItemsState(newItems);
    return newItems;
  }

  return {
    itemsToChange: items,
    itemsToChangeState: itemsState,
    addItemToChange({ id, quantity = 1, ingredients = [] }: ActionProps) {
      const result = updateItemsToChange({ id, quantity, ingredients });
      return result;
    },
    removeItemToChange({ id, quantity = -1, ingredients = [] }: ActionProps) {
      const result = updateItemsToChange({ id, quantity, ingredients });
      return result;
    },
    resetItemsToChange() {
      items.current = initialItemsToChange;
      setItemsState(initialItemsToChange);
      return initialItemsToChange;
    },
  };
}
